/// <amd-module name="Test/TestResultHistory/Components/HistoryView" />
import { SelectionMode } from "OfficeFabric/Selection";
import * as React from "react";
import { IViewContextData, TestOutcomeEnum, ViewContext } from "Test/common-util/Common";
import { getProjectName, TCMContractsConverter } from "Test/common-util/Common.Utils";
import { TestCaseResult, TestOutcome } from "Test/common-util/Contracts/Test";
import * as Telemetry from "Test/common-util/Telemetry";
import { HistoryViewActionsCreator } from "Test/TestResultHistory/Actions/HistoryViewActionCreator";
import { HistoryViewActionsHub, IGroupedHistoryItem } from "Test/TestResultHistory/Actions/HistoryViewActionHub";
import * as Resources from "Test/TestResultHistory/Resources";
import { HistoryViewSource } from "Test/TestResultHistory/Source/HistoryViewSource";
import { HistoryViewStore, IHistoryViewState } from "Test/TestResultHistory/Store/HistoryViewStore";
import * as TelemetryConstants from "Test/TestResultHistory/TelemetryConstants";
import * as Utils_String from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Accordion, IAccordionSection, IAccordionProps } from "VSSUI/Accordion";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { IListItemDetails, List, ListItem } from "VSSUI/List";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { IPickListItem, IPickListSelection, PickListDropdown } from "VSSUI/PickList";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { ago } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ObservableValue } from 'VSS/Core/Observable';
import { UncheckedObserver } from "VSSUI/Observer";

export interface HistogramBarData {
    value: number;
    title: string;
    state: string;
    selected?: boolean;
    actionArgs?: any;
    link: string;
}

export interface IHistoryViewProps extends IVssComponentProperties {
    testCaseResult: TestCaseResult;
    subResultId: number;
    viewContext: IViewContextData;
    isFullScreen: boolean;
}

export class HistoryView extends VssComponent<IHistoryViewProps, IHistoryViewState, IVssComponentContext> {
    private _pageContext: IVssComponentContext;
    private _store: HistoryViewStore;
    private _actionCreator: HistoryViewActionsCreator;
    private _lastButtonClicked: any;
    private _otherSideButtonOfLastButtonClicked: any;
    private _refTestHistoryTab: any;
    private _paneSize = new ObservableValue<number | undefined>(undefined);
    constructor(props: any, pageContext: IVssComponentContext) {
        super(props);
        const historyViewActionsHub = new HistoryViewActionsHub();
        const historyViewSource = new HistoryViewSource();
        this._store = new HistoryViewStore(historyViewActionsHub);
        this._pageContext = pageContext;
        this._actionCreator = new HistoryViewActionsCreator(this._pageContext.pageContext, historyViewActionsHub, historyViewSource);
        Telemetry.publishDetailsPaneEvents(
            this._pageContext.pageContext,
            this.props.viewContext,
            this.props.isFullScreen,
            TelemetryConstants.featureTestHistory_HistoryPaneViewed,
            {
                RunId: this.props.testCaseResult.testRun.id,
                ResultId: this.props.testCaseResult.id,
                SubresultId: this.props.subResultId,
                TestResultOutcome: this.props.testCaseResult.outcome
            }
        );
    }

    public componentDidUpdate(): void {

        if(!!this._lastButtonClicked && !!this._lastButtonClicked.current)
        {
            // Put focus only when button is not disabled.
            if(!this._lastButtonClicked.current.props.disabled)
            {
                this._lastButtonClicked.current.focus();
            }
            else
            {
                // If current button is disabled, then put focus on other side of the button.
                if(!!this._otherSideButtonOfLastButtonClicked && !!this._otherSideButtonOfLastButtonClicked.current)
                {
                    if(!this._otherSideButtonOfLastButtonClicked.current.props.disabled)
                    {
                        this._otherSideButtonOfLastButtonClicked.current.focus();
                    }
                }
            }
        }
        this._getCurrentWidthOfTestHistoryTab();
    }

    public componentWillMount(): void {
        this._handleStoreChange();
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._handleStoreChange);
        this._store.dispose();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._handleStoreChange);
        this._actionCreator.getBranches(this.props.testCaseResult, getProjectName(this.context.pageContext));
        this._actionCreator.fetchHistoryForBuildOrRelease(this.props.viewContext, this.props.testCaseResult);
        this._actionCreator.fetchHistoryForGroup(this.props.viewContext, this.props.testCaseResult);
        this._getCurrentWidthOfTestHistoryTab();
    }

    public render(): JSX.Element {
        const currentBuildItems: IAccordionSection[] = [
            {
                isCollapsed: new ObservableValue(false),
                key: "CurrentBuild",
                title: this.props.viewContext.viewContext === ViewContext.Build ? Resources.CurrentBuildDefinition : Resources.CurrentStage,
                onRenderContent: () => {
                    return (
                        <div>
                            {this.state.isHistoryForCurrentBuildOrEnvLoading && this._getLoadingHistoryView()}
                            {!this.state.isHistoryForCurrentBuildOrEnvLoading && (
                                <div>
                                    <hr className="history-group-separator" />
                                    <List
                                        itemProvider={new ArrayItemProvider(this.state.currentBuildOrEnvHistory)}
                                        renderRow={this._renderCurrentBuildOrEnvHistory}
                                        width="100%"
                                    />
                                </div>
                            )}
                        </div>
                    );
                }
            }
        ];

        const branchAccordionItems: IAccordionSection[] = [
            {
                isCollapsed: new ObservableValue(false),
                key: "BranchBuild",
                title: this.props.viewContext.viewContext === ViewContext.Build ? Resources.BranchesText : Resources.OtherStagesText,
                onRenderContent: () => {
                    return (
                        <div>
                            {this.state.isHistoryForGroupsLoading && this._getLoadingHistoryView()}
                            {!this.state.isHistoryForGroupsLoading && (
                                <div className="branches-groups">
                                    <hr className="history-group-separator" />
                                    <List
                                        itemProvider={new ArrayItemProvider(this.state.groupedHistoryItems)}
                                        renderRow={this._renderHistory}
                                        width="100%"
                                    />
                                </div>
                            )}
                        </div>
                    );
                }
            }
        ];

        if (this.state.errorMessage) {
            return (
                <div className="history-view-container">
                    <div className="history-view-error-message">
                        <MessageBar severity={MessageBarSeverity.Error} className="history-view-error-message-bar">
                            {this.state.errorMessage.toString()}
                        </MessageBar>
                    </div>
                </div>
            );
        } else {
            return (
                <div ref={(testResultDetailsContainer: HTMLDivElement) => (this._refTestHistoryTab = testResultDetailsContainer)}>
                    <UncheckedObserver paneSize={this._paneSize}>
                        {/* TODO: this should use a props object */}
                        {(paneSize: number | undefined) => {
                            if (paneSize === undefined) {
                                return null;
                            }
                            else {
                                return this._renderHistoryTab(currentBuildItems, branchAccordionItems);
                            }
                        }
                        }
                    </UncheckedObserver>
                </div> 
            );
        }
    }

    private _renderHistoryTab = (currentBuildItems: IAccordionSection[], branchAccordionItems: IAccordionSection[]): JSX.Element => {
        if (this._paneSize != undefined){
            const maxItemsToRender = this._getMaxItemsToRenderHistory();
            if (this.state.maxHistoryItemsToShow != maxItemsToRender){
                this._actionCreator.setMaxHistoryItemsToShow(maxItemsToRender);
                this._actionCreator.regenerateHistory();
            }
        }
        return (
            <div className="history-view-container">
            <div className="history-filters flex-row">
                <PickListDropdown
                    className="branches-picklist"
                    selectedItems={this.state.selectedBranch == null ? [] : [this.state.selectedBranch]}
                    getListItem={this._getListItem}
                    selectionMode={SelectionMode.single}
                    getPickListItems={this._getBrancheList}
                    isSearchable={true}
                    onSelectionChanged={this._onBranchSelected}
                    placeholder={Resources.BranchText}
                    searchTextPlaceholder={Resources.FilterByBranch}
                    ariaLabelFormat={Resources.FilterByBranch}
                    selectExactMatchOnEnter={true}
                />
                <div className="vss-FilterBar--action vss-FilterBar--action-clear clear-branch-filter">
                    <Button
                        className="filter-clear-button"
                        disabled={this.state.selectedBranch == null}
                        onClick={this._onResetBranchFilter}
                        iconProps={{ iconName: "Clear" }}
                        ariaLabel={Resources.ClearAssociatedAutomation}
                    >
                        {Resources.ClearAssociatedAutomation}
                    </Button>
                </div>
            </div>

            <div>
                <Accordion sections={currentBuildItems} className="current-build-accordion" onSectionClicked = {this._onSectionClicked}/>
                <Accordion className="branch-accordion" sections={branchAccordionItems} onSectionClicked = {this._onSectionClicked}/>
            </div>
        </div>
        );
    }

    private _getMaxItemsToRenderHistory = () : number => {
        const value = this._paneSize.value as number;
        let maxItemsToRender = 20;
        switch (true){
            case (value > 1366): maxItemsToRender = 100;
                                    break;
            case (value > 1024): maxItemsToRender = 80;
                                    break;
            case (value > 950): maxItemsToRender = 60;
                                    break;
            case (value > 650): maxItemsToRender = 40;
                                    break;
        }
        return maxItemsToRender;
    }

    private _getMaxPermissibleHeadingLength = () : number => {
        const value = this._paneSize.value as number;
        let maxPermissibleLength = 15;
        switch (true){
            case (value > 1800): maxPermissibleLength = 120;
                                    break;
            case (value > 1600): maxPermissibleLength = 80;
                                    break;
            case (value > 1366): maxPermissibleLength = 45;
                                    break;
            case (value > 1050): maxPermissibleLength = 35;
                                    break;
            case (value > 850): maxPermissibleLength = 30;
                                    break;
            case (value > 650): maxPermissibleLength = 25;
                                    break;
        }
        return maxPermissibleLength;
    }

    private _getCurrentWidthOfTestHistoryTab() {
        if(!!this._refTestHistoryTab){
            this._paneSize.value = this._refTestHistoryTab.clientWidth ;
        }
    }

    private _onSectionClicked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, section: IAccordionSection): void => {
        const collapsed = section.isCollapsed as ObservableValue<boolean>;
        collapsed.value = !collapsed.value;
    };

    private _getBrancheList = (): string[] => {
        if (!this.state.branches) {
            return [Resources.LoadingMessage];
        }
        if (this.state.branches.length === 0) {
            return [Resources.NoBranchesFound];
        }

        return this.state.branches;
    };

    private _onResetBranchFilter = (): void => {
        this._actionCreator.fetchHistoryForBuildOrRelease(this.props.viewContext, this.props.testCaseResult);
        this._actionCreator.fetchHistoryForGroup(this.props.viewContext, this.props.testCaseResult);
        Telemetry.publishDetailsPaneEvents(
            this._pageContext.pageContext,
            this.props.viewContext,
            this.props.isFullScreen,
            TelemetryConstants.featureTestHistory_BranchFilterCleared,
            {
                RunId: this.props.testCaseResult.testRun.id,
                ResultId: this.props.testCaseResult.id,
                SubresultId: this.props.subResultId
            }
        );
    };

    private _getListItem(item: string): IPickListItem {
        const disabled: boolean = item === Resources.NoBranchesFound || item === Resources.LoadingMessage;

        return {
            key: item,
            name: item,
            disabled: disabled
        };
    }

    private _onBranchSelected = (selection: IPickListSelection) => {
        const branchSelected = selection.selectedItems[0];
        this._actionCreator.fetchHistoryForBuildOrRelease(this.props.viewContext, this.props.testCaseResult, branchSelected);
        this._actionCreator.fetchHistoryForGroup(this.props.viewContext, this.props.testCaseResult, branchSelected);

        Telemetry.publishDetailsPaneEvents(
            this._pageContext.pageContext,
            this.props.viewContext,
            this.props.isFullScreen,
            TelemetryConstants.featureTestHistory_BranchFilterUsed,
            {
                RunId: this.props.testCaseResult.testRun.id,
                ResultId: this.props.testCaseResult.id,
                SubresultId: this.props.subResultId,
                BranchSelected: branchSelected
            }
        );
    };

    private _renderCurrentBuildOrEnvHistory = (
        index: number,
        item: IGroupedHistoryItem,
        details: IListItemDetails<IGroupedHistoryItem>
    ): JSX.Element => {
        let markerMargin = "";
        for (let i = item.leftIndex; i <= item.rightIndex; ++i) {
            if (
                item.historyForGroup.results[i].id === this.props.testCaseResult.id &&
                item.historyForGroup.results[i].testRun.id === this.props.testCaseResult.testRun.id
            ) {
                // Bar width is 6 and spacing is 1, so shift the marker by multiplying number of item with 8
                markerMargin = (8 * (this.state.maxHistoryItemsToShow - 1 - (i - item.leftIndex)) - 1).toString() + "px";
                break;
            }
        }
        let trimmedName = item.historyForGroup.displayName;
        if (this._paneSize != undefined){
            const maxPermissibleLength = this._getMaxPermissibleHeadingLength();
            if (item.historyForGroup.displayName.length > maxPermissibleLength){
                trimmedName = item.historyForGroup.displayName.substr(0, maxPermissibleLength).concat("..");
            }
        }
        const currentBuildArrowClassName: string = markerMargin != "" ? "current-build-blue-arrow" : "current-build-transparent-arrow";
        return (
            <ListItem className="history-view-list-item" details={details} index={index} key={"list-item" + new Date().getTime()}>
                <div className="current-build-or-env flex-row flex-center flex-grow">
                    <div className="history-information">
                        <div className="history-item-header flex-row fontWeightHeavy">
                            <Icon iconName="Build" />
                            <Tooltip text={item.historyForGroup.displayName}>
                            <div aria-label={item.historyForGroup.displayName} role="heading" className="groupTitle" aria-level={1}> {trimmedName} </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div className="history-chart flex-row flex-center">
                        <div className="navigation-icon-outer-container">
                            <Button
                                ref={item.prevButtonRef}
                                className={"navigation-icon-container"}
                                aria-disabled={item.prevButtonDisabled}
                                disabled={item.prevButtonDisabled}
                                onClick={() => this._leftArrowClicked(item)}
                                iconProps={{ iconName: "ChevronLeft" }}
                                ariaLabel={Resources.OlderResults}
                                tooltipProps={{ showOnFocus: true, text: Resources.OlderResults }}
                            />
                        </div>
                        <div className="history-chart-and-info flex-column">
                            <div className={currentBuildArrowClassName} style={{ width: "10px", paddingLeft: markerMargin }}>
                                <Tooltip text={Resources.CurrentResult}>
                                    {Icon({
                                        iconName: "CaretSolidDown",
                                        ariaLabel: "Current Testcase",
                                        className: currentBuildArrowClassName
                                    })}
                                </Tooltip>
                            </div>
                            {this._getHistogram(item.historyForGroup.results.slice(item.leftIndex, item.rightIndex + 1))}
                            <div className="window-dates flex-row">
                                <span className="leftDate">{item.historyForGroup.results[item.rightIndex].completedDate.toLocaleDateString()}</span>
                                <span className="rightDate">{item.historyForGroup.results[item.leftIndex].completedDate.toLocaleDateString()}</span>
                            </div>
                        </div>
                        <div className="navigation-icon-outer-container">
                            <Button
                                ref={item.nextButtonRef}
                                className={"navigation-icon-container"}
                                aria-disabled={item.nextButtonDisabled}
                                disabled={item.nextButtonDisabled}
                                onClick={() => this._rightArrowClicked(item)}
                                iconProps={{ iconName: "ChevronRight" }}
                                ariaLabel={Resources.NewerResults}
                                tooltipProps={{ showOnFocus: true, text: Resources.NewerResults }}
                            />
                        </div>
                    </div>
                </div>
            </ListItem>
        );
    };

    private _renderHistory = (index: number, item: IGroupedHistoryItem, details: IListItemDetails<IGroupedHistoryItem>): JSX.Element => {
        let trimmedName = item.historyForGroup.displayName;
        if (this._paneSize != undefined){
            const maxPermissibleLength = this._getMaxPermissibleHeadingLength();
            if (item.historyForGroup.displayName.length > maxPermissibleLength){
                trimmedName = item.historyForGroup.displayName.substr(0, maxPermissibleLength).concat("..");
            }
        }
        return (
            <ListItem className="history-view-list-item" details={details} index={index} key={"list-item" + new Date().getTime()}>
                <div className="history-group-item flex-row flex-center flex-grow">
                    <div className="history-information">
                        <div className="history-item-header flex-row fontWeightHeavy">
                            <Icon iconName={this._getIconNameForGroupedHistory()} />
                            <Tooltip text={item.historyForGroup.displayName}>
                                <div aria-label={item.historyForGroup.displayName} role="heading" className="groupTitle" aria-level={1}> {trimmedName} </div>
                            </Tooltip>
                        </div>
                    </div>
                    <div className="history-chart flex-row flex-center">
                        <div className="navigation-icon-outer-container">
                            <Button
                                ref={item.prevButtonRef}
                                className={"navigation-icon-container"}
                                aria-disabled={item.prevButtonDisabled}
                                disabled={item.prevButtonDisabled}
                                onClick={() => this._leftArrowClicked(item, index)}
                                iconProps={{ iconName: "ChevronLeft" }}
                                ariaLabel={Resources.OlderResults}
                                tooltipProps={{ showOnFocus: true, text: Resources.OlderResults }}
                            />
                        </div>
                        <div className="history-chart-and-info flex-column">
                            {this._getHistogram(item.historyForGroup.results.slice(item.leftIndex, item.rightIndex + 1))}
                            <div className="window-dates flex-row">
                                <span className="leftDate">{item.historyForGroup.results[item.rightIndex].completedDate.toLocaleDateString()}</span>
                                <span className="rightDate">{item.historyForGroup.results[item.leftIndex].completedDate.toLocaleDateString()}</span>
                            </div>
                        </div>
                        <div className="navigation-icon-outer-container">
                            <Button
                                ref={item.nextButtonRef}
                                className={"navigation-icon-container"}
                                aria-disabled={item.nextButtonDisabled}
                                disabled={item.nextButtonDisabled}
                                onClick={() => this._rightArrowClicked(item, index)}
                                iconProps={{ iconName: "ChevronRight" }}
                                ariaLabel={Resources.NewerResults}
                                tooltipProps={{ showOnFocus: true, text: Resources.NewerResults }}
                            />
                        </div>
                    </div>
                </div>
            </ListItem>
        );
    };
    private _getLoadingHistoryView(): JSX.Element {
        return (
            <div className="history-view-pane-loading">
                <Spinner
                    ariaLabel={Resources.LoadingHistoryMessage}
                    className="history-view-loading-spinner test-result-loading-spinner-separator"
                    size={SpinnerSize.large}
                    label={Resources.LoadingHistoryMessage}
                />
            </div>
        );
    }

    private _getIconNameForGroupedHistory(): string {
        const iconName: string = this.props.viewContext.viewContext === ViewContext.Build ? "BranchMerge" : "Build";
        return iconName;
    }

    private _leftArrowClicked = (item: IGroupedHistoryItem, index?: number): any => {
        // Set current clicked button ref to set focus
        this._lastButtonClicked = item.prevButtonRef;
        this._otherSideButtonOfLastButtonClicked = item.nextButtonRef;

        Telemetry.publishDetailsPaneEvents(
            this._pageContext.pageContext,
            this.props.viewContext,
            this.props.isFullScreen,
            TelemetryConstants.featureTestHistory_LeftOrRightArrowClicked,
            {
                RunId: this.props.testCaseResult.testRun.id,
                ResultId: this.props.testCaseResult.id,
                SubresultId: this.props.subResultId,
                Group: item.historyForGroup.displayName,
                ButtonClicked: "Left"
            }
        );
        if (item.rightIndex < item.historyForGroup.results.length - 1) {
            this._actionCreator.getNextOrPreviousResults(
                this.props.viewContext,
                this.props.testCaseResult,
                item,
                true,
                false,
                this.state.selectedBranch,
                index
            );
        } else {
            this._actionCreator.getNextOrPreviousResults(
                this.props.viewContext,
                this.props.testCaseResult,
                item,
                true,
                true,
                this.state.selectedBranch,
                index
            );
        }
    };

    private _rightArrowClicked = (item: IGroupedHistoryItem, index?: number): any => {
        // Set current clicked button ref to set focus
        this._lastButtonClicked = item.nextButtonRef;
        this._otherSideButtonOfLastButtonClicked = item.prevButtonRef;

        Telemetry.publishDetailsPaneEvents(
            this._pageContext.pageContext,
            this.props.viewContext,
            this.props.isFullScreen,
            TelemetryConstants.featureTestHistory_LeftOrRightArrowClicked,
            {
                RunId: this.props.testCaseResult.testRun.id,
                ResultId: this.props.testCaseResult.id,
                SubresultId: this.props.subResultId,
                Group: item.historyForGroup.displayName,
                ButtonClicked: "Right"
            }
        );
        if (item.leftIndex > 0) {
            this._actionCreator.getNextOrPreviousResults(
                this.props.viewContext,
                this.props.testCaseResult,
                item,
                false,
                false,
                this.state.selectedBranch,
                index
            );
        } else {
            this._actionCreator.getNextOrPreviousResults(
                this.props.viewContext,
                this.props.testCaseResult,
                item,
                false,
                true,
                this.state.selectedBranch,
                index
            );
        }
    };

    private _getHistogram(results: TestCaseResult[]): JSX.Element {
        return (
            <LegacyComponent
                wrappedType="LegacyHistogram"
                modules={["TestManagement/Scripts/Scenarios/Common/DetailsPanel/ControllerViews/LegacyHistogram"]}
                loadingComponent={() => <Spinner className="about-loading-spinner" />}
                {...this._getHistogramProps(results)}
            />
        );
    }

    private _getHistogramProps(results: TestCaseResult[]) {
        let histogramData: HistogramBarData[];
        histogramData = this._getHistogramData(results);
        const historyProps = {
            cssClass: "build-histogram definition-histogram",
            barCount: this.state.maxHistoryItemsToShow,
            barWidth: 6,
            barHeight: 35,
            barSpacing: 2,
            selectedState: "selected",
            hoverState: "hover",
            items: histogramData
        };
        return historyProps;
    }

    private _getHistogramData(results: TestCaseResult[]): HistogramBarData[] {
        if (!results || results.length === 0) {
            return [];
        }

        let maxDuration = Number.MIN_VALUE;

        const items: HistogramBarData[] = results.map((result: TestCaseResult) => {
            const duration: any = result.durationInMs ? result.durationInMs : 0;
            maxDuration = Math.max(maxDuration, duration);
            let title: string;
            if (result.releaseReference.id === 0) {
                title = Utils_String.format(
                    Resources.ResultHistoryHistogramBarTitleText,
                    result.outcome,
                    ago(result.completedDate),
                    result.buildReference.number,
                    TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(duration)
                );
            } else {
                title = Utils_String.format(
                    Resources.ResultHistoryHistogramBarTitleText,
                    result.outcome,
                    ago(result.completedDate),
                    result.releaseReference.name,
                    TCMContractsConverter.ConvertMilliSecondsToReadableFormatForResultSummary(duration)
                );
            }
            const redirectUrl =
                result.releaseReference.id === 0
                    ? this._getNewBuildSummaryTestResultUrl(result.buildReference.id, parseInt(result.testRun.id), result.id)
                    : this._constructReleaseTestResultUrl(result);

            return {
                value: duration,
                state: this._getTextClassNameForTestResult(result),
                title: title,
                link: redirectUrl
            };
        });

        return items;
    }

    private _getNewBuildSummaryTestResultUrl(buildId: number, runId: number, resultId: number, paneView?: string): string {
        const newBuildHubRoute = "ms.vss-build-web.ci-results-hub-route";
        const testExtensionViewId = "ms.vss-test-web.build-test-results-tab";
        const routeValues: { [key: string]: string } = {};

        routeValues["buildId"] = buildId.toString();
        routeValues["view"] = testExtensionViewId;
        routeValues["project"] = getProjectName(this.context.pageContext);
        routeValues["runId"] = runId.toString();
        routeValues["resultId"] = resultId.toString();

        return routeUrl(this.context.pageContext, newBuildHubRoute, routeValues);
    }

    private _constructReleaseTestResultUrl(result: TestCaseResult): string {
        let redirectUrl = "";
        try {
            redirectUrl = routeUrl(this._pageContext.pageContext, "ms.vss-releaseManagement-web.cd-release-progress-default-route", {
                project: getProjectName(this.context.pageContext),
                releaseId: result.releaseReference.id.toString(),
                extensionId: "ms.vss-test-web.test-result-in-release-environment-editor-tab",
                _a: "release-environment-extension",
                environmentId: result.releaseReference.environmentId.toString(),
                runId: result.testRun.id,
                resultId: result.id.toString()
            });
        } catch (ex) {
            console.log(ex);
        }
        return redirectUrl;
    }

    private _getTextClassNameForTestResult(result: TestCaseResult) {
        if (TestOutcomeEnum[TestOutcome.Failed] === result.outcome) {
            return "failed";
        } else if (TestOutcomeEnum[TestOutcome.Passed] === result.outcome) {
            return "succeeded";
        } else if (TestOutcomeEnum[TestOutcome.Inconclusive] === result.outcome || TestOutcomeEnum[TestOutcome.NotExecuted] === result.outcome) {
            return "partiallysucceeded";
        } else {
            return "default";
        }
    }

    private _handleStoreChange = (): void => {
        this.setState(this._store.getState());
    };
}

Components.add("test-resulthistory", HistoryView);
